import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox, Input } from '@/components/ui'
import { ChevronDown, ChevronUp, Plus, Trash, User } from 'lucide-react'
import Loader from '../../common/components/Loader'
import { Artist, ArtistDetails, Languages } from '../../common/models'
import { IColumn, Table } from '../../core/components/Table'
import { sanitizeDate } from '../../core/constants/times'
import { useTableParams } from '../../core/hooks/table-params'
import { useCheckPermission } from '../../core/hooks/use-check-permission'
import {
  ArtistParams,
  deleteArtist,
  getAllArtists,
  getArtist,
  publishArtist,
  unpublishArtist,
  updateArtist,
} from '../../core/services/artist'
import { getSettingsLanguages } from '../../core/services/festival-settings'
import { useAppSelector } from '../../store'
import { CreateArtistModal } from './CreateArtistModal'
import { DeleteConfirmationModal } from './DeleteConfirmationModal'
import { MessageBox } from './messages/MessageBox'
import { Pagination } from './Pagination'
import { Icon } from '@/common/components/Icon'
import { ActionsMenu } from '@/components/ActionsMenu'
import { cn } from '@/lib/utils'
import { find } from 'lodash'

export type TableSearchParams = Record<string, string | null | undefined>

export const ArtistsPage: React.FC = () => {
  const {
    page,
    setPage,
    size,
    setSize,
    sortBy,
    setSortBy,
    totalElements,
    setTotalElemenets,
    searchParams,
  } = useTableParams()

  const { hasEditPermission } = useCheckPermission({ page: 'ARTISTS' })

  const [showCreateArtistModal, setShowCreateArtistModal] =
    useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [allLanguages, setAllLanguages] = useState<Languages[]>([])
  const [currentArtist, setCurrentArtist] = useState<ArtistDetails>()
  const [artists, setArtists] = useState<Artist[]>([])
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [editedWeight, setEditedWeight] = useState<string>('')
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})
  const searchInput = useRef(null)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const editedWeightArtistIndex = useRef<number>()
  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const allLanguagesResponse = await getSettingsLanguages()
        if (allLanguagesResponse.status === 200) {
          setAllLanguages(allLanguagesResponse.data)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [editionId, festivalName])

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const artistResponse = await getAllArtists({
        page,
        size,
        sortBy,
        sortDirection,
        name: searchParams.current.name,
      })

      setArtists(artistResponse.data.content)
      setTotalElemenets(artistResponse.data.totalElements)
      setSize(artistResponse.data.size)
      setPage(artistResponse.data.pageNumber + 1)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [
    page,
    size,
    sortDirection,
    sortBy,
    setPage,
    setSize,
    setTotalElemenets,
    editionId,
    festivalName,
  ])

  useEffect(() => {
    getData()
  }, [getData])

  const handleDelete = () => {
    if (!hasEditPermission) {
      return
    }
    if (isCheck.length > 0) {
      setDeleteConfirmationModal(true)
    }
  }

  const deleteFunc = () => {
    // prettier-ignore
    (async () => {
      try {
        const { status } = await deleteArtist(isCheck)
        if (status === 204) {
          const response = await getAllArtists({ page, size, sortBy })
          setArtists(response.data.content)
          setTotalElemenets(response.data.totalElements)
          setSize(response.data.size)
          if (response.data.totalPages <= page) {
            setPage(response.data.pageNumber)
          } else {
            setPage(response.data.pageNumber + 1)
          }
          setIsCheck([])
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }

  const handlePublish = (isCheck: string[]) => {
    if (!hasEditPermission) {
      return
    }
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const { status } = await publishArtist(isCheck)
        if (status === 200) {
          getData()
          setIsCheck([])
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Publish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const handleUnpublish = (isCheck: string[]) => {
    if (!hasEditPermission) {
      return
    }
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const { status } = await unpublishArtist(isCheck)
        if (status === 200) {
          getData()
          setIsCheck([])
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Unpublish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(artists.map((art) => art.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('ASC')
      setSortBy(sort)
    }
  }

  const handleSearchParams = useCallback(
    async (col: IColumn, q: string | null) => {
      searchParams.current = {
        ...searchParams.current,
        [col.accessor]: q,
      }

      setLoading(true)
      try {
        const params: ArtistParams = {
          page: 0,
          size,
          sortBy,
          sortDirection,
        }

        params.name = searchParams.current.name

        const { data: res, status } = await getAllArtists(params)
        if (status === 200) {
          setArtists(res.content)
          setTotalElemenets(res.totalElements)
          setSize(res.size)
          setPage(res.pageNumber + 1)
        }
      } catch (err) {
        console.log(err)
      } finally {
        setLoading(false)
      }
    },
    [page, size],
  )

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const handleBlur = async (artist: Artist) => {
    if (!editedWeight || artist.weight === parseInt(editedWeight)) {
      return
    }
    try {
      const { status } = await updateArtist(artist.id, {
        weight: parseInt(editedWeight),
      })
      getData()
      setEditedWeight('')
      editedWeightArtistIndex.current = undefined
      if (status === 200) {
        setArtists(
          artists.map((art) =>
            art.id === artist.id
              ? { ...art, weight: parseInt(editedWeight) }
              : art,
          ),
        )
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),
    },
    {
      title: 'Name',
      accessor: 'name',
      sortable: true,
      searchable: true,
      render: (row) => {
        return (
          <div className="text-subtitle-m min-w-[220px] text-monochrome-1">
            {row.name}
          </div>
        )
      },
    },
    {
      title: 'Image',
      accessor: 'image',
      render: (row) => {
        return row.thumbnailLink ? (
          <img
            src={row.thumbnailLink}
            className="rounded-2xl min-w-[68px] h-[68px] object-contain shrink-0"
          />
        ) : (
          <div className="w-[68px] h-[68px] rounded-2xl flex justify-center items-center font-size-20 shrink-0">
            <User className="w-6 h-6" />
          </div>
        )
      },
    },
    {
      title: 'Description',
      accessor: 'description',
      render: (row) => {
        const plainText = row.description?.replace(/<[^>]*>/g, '') || ''
        return (
          <div className="bg-white/10 py-2.5 px-[14px] rounded-[10px] min-w-[260px] max-w-[300px] text-body-small">
            <div className="line-clamp-3">{plainText}</div>
          </div>
        )
      },
    },
    {
      title: 'Default IMG',
      accessor: '',
      render: (row) => {
        return (
          <Checkbox
            onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
            checked={row.defaultImage}
            disabled
          />
        )
      },
    },
    {
      title: 'Socials',
      accessor: 'description',
      render: (row) => {
        const socials = [
          { label: 'Facebook', checked: Boolean(row.hasFacebook) },
          { label: 'X', checked: Boolean(row.hasTwitter) },
          { label: 'Website', checked: Boolean(row.hasWebsite) },
          { label: 'Youtube', checked: Boolean(row.hasYoutube) },
          { label: 'Instagram', checked: Boolean(row.hasInstagram) },
          { label: 'TikTok', checked: Boolean(row.hasTiktok) },
        ]
        const isExpanded = Boolean(expandedRows[row.id])

        return (
          <div className="min-w-[180px]">
            <div
              className={cn(
                'grid grid-cols-2 gap-x-6 gap-y-2 h-[20px] overflow-hidden',
                isExpanded && 'h-[40px]',
              )}
            >
              {socials.map((s) => (
                <div key={s.label} className="flex items-center gap-2">
                  <Checkbox checked={s.checked} disabled />
                  <span className="text-body-medium text-monochrome-2">
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
            <button
              onClick={() =>
                setExpandedRows((prev) => ({
                  ...prev,
                  [row.id]: !prev[row.id],
                }))
              }
              className="flex text-body-medium items-center gap-1 text-sm text-monochrome-2 mt-2 cursor-pointer"
            >
              More{' '}
              {isExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
          </div>
        )
      },
    },
    {
      title: 'Weight',
      accessor: 'weight',
      sortable: true,
      render: (row, index) => (
        <Input
          value={
            editedWeightArtistIndex.current === index
              ? editedWeight
              : row.weight
          }
          onChange={(e) => {
            setEditedWeight(e.target.value)
          }}
          onBlur={() => handleBlur(row)}
          onFocus={() => {
            setEditedWeight(row.weight.toString())
            editedWeightArtistIndex.current = index
          }}
          className="bg-white/10 border-none min-w-[100px]"
          ref={searchInput}
          readOnly={!hasEditPermission}
        />
      ),
    },
    {
      title: 'Published',
      accessor: 'published',
      sortable: true,
      render: (row) => (
        <Checkbox
          onCheckedChange={() => {
            if (row.published) {
              handleUnpublish([row.id])
            } else {
              handlePublish([row.id])
            }
          }}
          checked={row.published}
          disabled={!hasEditPermission}
        />
      ),
    },
    {
      title: 'Publish Date',
      accessor: 'publishedAt',
      sortable: true,
      render: (row) => (
        <span className="whitespace-nowrap">
          {sanitizeDate(row.publishedAt)}
        </span>
      ),
    },
    {
      title: 'Edit',
      accessor: '',
      render: (row) => (
        <a
          className="view text-accent-1 underline"
          href="#"
          onClick={async (e) => {
            e.preventDefault()
            if (!hasEditPermission) {
              return
            }
            setShowCreateArtistModal(true)
            setEditMode(true)
            setLoading(true)
            const response = await getArtist(row.id)
            setCurrentArtist(response.data)
            setLoading(false)
          }}
        >
          Edit
        </a>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle="Artists"
        actions={
          <>
            {hasEditPermission && isCheck.length >= 1 && (
              <>
                <ActionsMenu
                  actions={[
                    { label: 'Publish', onClick: () => handlePublish(isCheck) },
                    {
                      label: 'Unpublish',
                      onClick: () => handleUnpublish(isCheck),
                    },
                  ]}
                />

                <Button variant="destructive" onClick={handleDelete}>
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              </>
            )}
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setEditMode(false)
                  setShowCreateArtistModal(true)
                  setCurrentArtist(undefined)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Artists" className="mb-5" icon="artists-bold" />
        <Table
          className="[&_th]:!whitespace-nowrap"
          handleSortBy={handleSortBy}
          handleSearch={(col, query) => {
            handleSearchParams(col, query)
          }}
          searchParams={searchParams.current}
          isCheck={isCheck}
          columns={columns}
          data={artists}
          rowReorder
          alignment="middle"
          onRowReorder={(next) => setArtists(next)}
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <CreateArtistModal
        allLanguages={allLanguages}
        artist={currentArtist}
        setShowEditModal={setShowCreateArtistModal}
        setArtists={setArtists}
        showEditModal={showCreateArtistModal}
        editMode={editMode}
        page={page}
        setPage={setPage}
        size={size}
        setTotalElemenets={setTotalElemenets}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={isCheck.length > 1 ? 'Artists' : 'Artist'}
        deleteFunc={deleteFunc}
        deletedItem={
          isCheck.length > 1
            ? `${isCheck.length} artists`
            : find(artists, { id: isCheck[0] })?.name
        }
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
